import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  getImpersonationDomain,
  setImpersonationDomain,
  subscribeToImpersonationChanges,
} from "./storage";
import { useAuthContext } from "../auth/hooks";

interface ImpersonationContextType {
  impersonationDomain: string | null;
  isImpersonating: boolean;
  /** Begin viewing the app, read-only, as `domain`. Remounts the route tree so
   *  every page refetches scoped to the new domain. */
  startImpersonation: (domain: string) => void;
  /** Stop impersonating and return to your own account. Remounts the route tree. */
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType>({
  impersonationDomain: null,
  isImpersonating: false,
  startImpersonation: () => {},
  stopImpersonation: () => {},
});

export const useImpersonation = () => useContext(ImpersonationContext);

export const ImpersonationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isLoading, isStaff } = useAuthContext();
  const [impersonationDomain, setDomainState] = useState<string | null>(() =>
    getImpersonationDomain()
  );

  // Invariant: a "view as customer" session can only exist while the logged-in
  // user is staff. Once auth has resolved, any non-staff session is forced out
  // of impersonation — this is what makes a stale target impossible to inherit
  // across a logout → login-as-customer in the same tab (and on shared
  // machines). It is load-bearing now that the target is persisted to
  // localStorage: `setImpersonationDomain(null)` clears that backing store, so a
  // value can't survive into a non-staff session. Gated on `!isLoading` so we
  // don't clobber a legitimate staff session during the brief auth-loading
  // window where `isStaff` is still false (e.g. on a refresh).
  useEffect(() => {
    if (!isLoading && !isStaff && getImpersonationDomain() !== null) {
      setImpersonationDomain(null);
      setDomainState(null);
    }
  }, [isLoading, isStaff]);

  // Keep this tab in sync when the scope is changed in another tab (start, exit,
  // or sign-out). Without this, a tab open at the time of an exit/sign-out in a
  // sibling tab would keep showing the stale "Viewing as…" banner and scope.
  useEffect(() => subscribeToImpersonationChanges(setDomainState), []);

  const startImpersonation = useCallback((domain: string) => {
    const normalized = domain.trim().toLowerCase();
    if (!normalized) return;
    // Update the out-of-React singleton first, so when the route tree remounts
    // on the resulting key change (see AppRouter), its first fetch already reads
    // the new scope.
    setImpersonationDomain(normalized);
    setDomainState(normalized);
  }, []);

  const stopImpersonation = useCallback(() => {
    setImpersonationDomain(null);
    setDomainState(null);
  }, []);

  return (
    <ImpersonationContext.Provider
      value={{
        impersonationDomain,
        isImpersonating: impersonationDomain !== null,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};
